import { z } from "zod";

export const ContractMatchTargetTypeValidation = z.union([
  z.literal("general_contract"),
  z.literal("trading_contract"),
]);

export const ContractMatchProposalsQueryValidation = z.object({
  page: z.number().int().positive().default(1),
  page_size: z.number().int().positive().max(100).default(50),
  target_type: ContractMatchTargetTypeValidation,
}).strict();

export const ListContractChangesQueryValidation = z.object({
  page: z.number().int().positive(),
  page_size: z.number().int().positive().max(100),
  match_status: z.literal("unmatched"),
  archived: z.literal("false"),
  schema_version: z.number().int().positive(),
}).strict();

const GeneralPairContractChangeItemValidation = z.object({
  change_id: z.string().uuid(),
  target_type: z.literal("general_contract"),
  target_id: z.string().uuid(),
}).strict();

const TradingPairContractChangeItemValidation = z.object({
  change_id: z.string().uuid(),
  target_type: z.literal("trading_contract"),
  target_id: z.string().uuid(),
}).strict();

export const PairContractChangeItemValidation = z.discriminatedUnion("target_type", [
  GeneralPairContractChangeItemValidation,
  TradingPairContractChangeItemValidation,
]);

export type ContractMatchTargetType = z.infer<typeof ContractMatchTargetTypeValidation>;
export type ContractMatchProposalsQuery = z.infer<typeof ContractMatchProposalsQueryValidation>;
export type ListContractChangesQuery = z.infer<typeof ListContractChangesQueryValidation>;
export type PairContractChangeItem = z.infer<typeof PairContractChangeItemValidation>;

export const BulkPairContractChangesRequestValidation = z.object({
  items: z.array(PairContractChangeItemValidation).min(1).max(100),
}).strict().superRefine((data: { items: PairContractChangeItem[] }, context: z.RefinementCtx): void => {
  const changeIds = new Set<string>();
  const targetIdentities = new Set<string>();

  data.items.forEach((item: PairContractChangeItem, index: number): void => {
    const targetIdentity = `${item.target_type}:${item.target_id}`;
    if (changeIds.has(item.change_id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Duplicate Agtech contract changes are not allowed",
        path: ["items", index, "change_id"],
      });
    }
    if (targetIdentities.has(targetIdentity)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Duplicate target contracts are not allowed",
        path: ["items", index, "target_id"],
      });
    }
    changeIds.add(item.change_id);
    targetIdentities.add(targetIdentity);
  });
});

export type BulkPairContractChangesRequest = z.infer<typeof BulkPairContractChangesRequestValidation>;
